/**
 * Firebase Configuration - SuperJarvis User Tier
 *
 * This is the "Body" layer - handles user identity and state.
 * The "Brain" (GCP Cloud Run) handles the intelligence.
 *
 * Authentication Methods:
 * - Magic Link (Email Link) - passwordless sign-in via email
 * - Google Sign-In - one-click authentication with Google account
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import {
  getAuth,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  Auth,
  ActionCodeSettings,
  GoogleAuthProvider,
  signInWithPopup,
  OAuthCredential,
  getRedirectResult,
  browserLocalPersistence,
  setPersistence
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Firestore,
  Timestamp
} from 'firebase/firestore';

// Firebase configuration for agentbuilder-480107
// authDomain: Must match the hosting domain for redirects to work properly
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: window.location.hostname === 'localhost' ? 'agentbuilder-480107.firebaseapp.com' : window.location.hostname,
  projectId: 'agentbuilder-480107',
  storageBucket: 'agentbuilder-480107.appspot.com',
  messagingSenderId: '927047522832',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || ''
};

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;

// Promise that resolves when auth is fully initialized with persistence
let authReadyPromise: Promise<void>;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);

  // Set persistence to LOCAL so user stays logged in across browser sessions
  // This prevents the need to re-login every time the app is opened
  // IMPORTANT: We await this before considering auth ready
  authReadyPromise = setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log('[Firebase] Auth initialized with LOCAL persistence');
    })
    .catch((error: Error) => {
      console.error('[Firebase] Failed to set persistence:', error);
      // Continue anyway - will use default persistence
    });
} catch (error: any) {
  console.error('[Firebase] Initialization error:', error);
  authReadyPromise = Promise.resolve(); // Resolve even on error to prevent hanging
}

/**
 * Wait for Firebase Auth to be fully initialized with persistence.
 * Call this before relying on auth.currentUser or onAuthStateChanged.
 */
export async function waitForAuthReady(): Promise<void> {
  await authReadyPromise;
}

// Google Auth Provider with recommended scopes
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
// Set custom parameters for better UX
googleProvider.setCustomParameters({
  prompt: 'select_account'  // Always show account selector
});

// Local storage key for email (needed to complete sign-in)
const EMAIL_STORAGE_KEY = 'superjarvis_signin_email';

// Action code settings for email link
const getActionCodeSettings = (): ActionCodeSettings => ({
  // URL to redirect back to after clicking the link
  url: window.location.origin,
  handleCodeInApp: true
});

// ============================================================================
// AUTHENTICATION: Magic Link (Email)
// ============================================================================

/**
 * Send Magic Link to email
 */
export async function sendMagicLink(email: string): Promise<void> {
  try {
    await sendSignInLinkToEmail(auth, email, getActionCodeSettings());
    // Store email locally to complete sign-in when user clicks link
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
    console.log('[Firebase] Magic link sent to:', email);
  } catch (error) {
    console.error('[Firebase] Error sending magic link:', error);
    throw error;
  }
}

/**
 * Check if current URL is a sign-in link
 */
export function isEmailSignInLink(): boolean {
  return isSignInWithEmailLink(auth, window.location.href);
}

/**
 * Complete sign-in with email link
 */
export async function completeEmailSignIn(email?: string): Promise<User | null> {
  try {
    // Get email from parameter or localStorage
    const signInEmail = email || window.localStorage.getItem(EMAIL_STORAGE_KEY);

    if (!signInEmail) {
      throw new Error('Please enter your email to complete sign-in');
    }

    const result = await signInWithEmailLink(auth, signInEmail, window.location.href);
    const user = result.user;

    // Clear stored email
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);

    // Clean up URL (remove sign-in parameters)
    window.history.replaceState({}, document.title, window.location.pathname);

    // Create/update user profile in Firestore
    await ensureUserProfile(user);

    console.log('[Firebase] User signed in via Magic Link:', user.email);
    return user;
  } catch (error) {
    console.error('[Firebase] Sign-in error:', error);
    throw error;
  }
}

/**
 * Get stored email for sign-in completion
 */
export function getStoredEmail(): string | null {
  return window.localStorage.getItem(EMAIL_STORAGE_KEY);
}

// ============================================================================
// AUTHENTICATION: Google Sign-In
// ============================================================================

/**
 * Sign in with Google (popup only)
 * 
 * Uses popup authentication only - more reliable than redirect flow
 * which has issues with cross-domain cookies and auth handlers.
 */
export async function signInWithGoogle(): Promise<User> {
  try {
    console.log('[Firebase] Starting Google sign-in with popup...');
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Create/update user profile in Firestore
    await ensureUserProfile(user);

    console.log('[Firebase] User signed in via Google:', user.email);
    return user;
  } catch (error: unknown) {
    const firebaseError = error as { code?: string; message?: string };
    console.error('[Firebase] Google sign-in error:', firebaseError.code, firebaseError.message);

    // Provide user-friendly error messages
    if (firebaseError.code === 'auth/popup-blocked') {
      throw new Error('Popup was blocked. Please allow popups for this site and try again.');
    } else if (firebaseError.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled.');
    } else if (firebaseError.code === 'auth/cancelled-popup-request') {
      throw new Error('Sign-in was cancelled.');
    }

    throw error;
  }
}

// ============================================================================
// GOOGLE SERVICE CONNECTIONS (Gmail, Calendar, Drive)
// Uses Firebase Auth popup with additional OAuth scopes
// No separate redirect URI needed — Firebase handles everything
// ============================================================================

const SERVICE_SCOPES: Record<string, string[]> = {
  gmail: [
    'https://www.googleapis.com/auth/gmail.readonly',
    'https://www.googleapis.com/auth/gmail.send',
  ],
  calendar: [
    'https://www.googleapis.com/auth/calendar.events',
    'https://www.googleapis.com/auth/calendar.readonly',
  ],
  drive: [
    'https://www.googleapis.com/auth/drive.file',
    'https://www.googleapis.com/auth/drive.readonly',
  ],
  sheets: [
    'https://www.googleapis.com/auth/spreadsheets',
    'https://www.googleapis.com/auth/drive.file',
  ],
};

/**
 * Connect a Google service (Gmail, Calendar, Drive) by requesting additional OAuth scopes.
 * Uses Firebase Auth popup — no separate OAuth redirect URI needed.
 * Returns the Google OAuth access token for API calls.
 */
export async function connectGoogleService(
  service: 'gmail' | 'calendar' | 'drive' | 'sheets'
): Promise<{ accessToken: string; refreshToken?: string; user: User }> {
  const scopes = SERVICE_SCOPES[service];
  if (!scopes) throw new Error(`Unknown service: ${service}`);

  // Create a new Google provider with the additional scopes
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  for (const scope of scopes) {
    provider.addScope(scope);
  }
  // Force consent screen so Google actually grants the calendar/email scopes.
  // Without prompt:'consent', signInWithPopup re-uses the existing Firebase session
  // and the returned accessToken won't include the additional scopes.
  provider.setCustomParameters({
    login_hint: auth.currentUser?.email || '',
    prompt: 'consent',
  });

  try {
    console.log(`[Firebase] Connecting ${service} with scopes:`, scopes);
    const result = await signInWithPopup(auth, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result) as OAuthCredential | null;

    if (!credential?.accessToken) {
      throw new Error('No access token received from Google');
    }

    console.log(`[Firebase] ${service} connected, got access token`);

    // Store the token in orbit-gateway's Identity Vault for backend API calls
    const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
    const idToken = await result.user.getIdToken();

    // Step 1: Store the token
    const storeResp = await fetch(`${ORBIT_GATEWAY_URL}/integrations/store-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        provider: service,
        access_token: credential.accessToken,
      }),
    });

    if (!storeResp.ok) {
      const errBody = await storeResp.text().catch(() => '');
      throw new Error(
        `Failed to store ${service} token in vault (${storeResp.status}): ${errBody}`
      );
    }

    // Step 2: Verify the token is actually usable by making one real API call
    const verifyResp = await fetch(`${ORBIT_GATEWAY_URL}/integrations/verify-connection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ provider: service }),
    });

    if (!verifyResp.ok) {
      console.warn(`[Firebase] ${service} token stored but verification failed — connection may not work`);
      // Don't block — the token was stored. Backend verification endpoint may not exist yet.
      // But log it so we know.
    } else {
      const verifyData = await verifyResp.json().catch(() => ({}));
      console.log(`[Firebase] ${service} connection verified:`, verifyData);
    }

    return {
      accessToken: credential.accessToken,
      user: result.user,
    };
  } catch (error: unknown) {
    const firebaseError = error as { code?: string; message?: string };
    console.error(`[Firebase] ${service} connection error:`, firebaseError.code, firebaseError.message);

    if (firebaseError.code === 'auth/popup-blocked') {
      throw new Error('Popup was blocked. Please allow popups for this site.');
    } else if (firebaseError.code === 'auth/popup-closed-by-user') {
      throw new Error('Connection was cancelled.');
    }
    throw error;
  }
}

/**
 * Handle Google Sign-In redirect result
 * 
 * Call this on app initialization to handle returning from
 * Google Sign-In redirect flow.
 */
export async function handleGoogleRedirectResult(): Promise<User | null> {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const user = result.user;
      await ensureUserProfile(user);
      console.log('[Firebase] User signed in via Google redirect:', user.email);
      return user;
    }
    return null;
  } catch (error) {
    console.error('[Firebase] Google redirect result error:', error);
    return null;
  }
}

// ============================================================================
// AUTHENTICATION: Common Functions
// ============================================================================

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(auth);
    console.log('[Firebase] User signed out');
  } catch (error) {
    console.error('[Firebase] Sign-out error:', error);
    throw error;
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Get current user
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Get Firebase ID token for GCP authentication
 */
export async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    return await user.getIdToken();
  } catch (error) {
    console.error('[Firebase] Error getting ID token:', error);
    return null;
  }
}

// ============================================================================
// FIRESTORE: User Context (Hot Memory)
// ============================================================================

interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  createdAt: Timestamp;
  lastSeen: Timestamp;
  authProvider: 'email' | 'google';
  preferences: {
    theme: 'dark' | 'light';
    ghostMode: boolean;
  };
}

interface UserContext {
  lastIntent: string | null;
  lastAgent: string | null;
  sessionStart: Timestamp;
  messageCount: number;
}

/**
 * Ensure user profile exists in Firestore
 */
async function ensureUserProfile(user: User): Promise<void> {
  const userRef = doc(db, 'superjarvis_users', user.uid);
  const userDoc = await getDoc(userRef);

  // Determine auth provider
  const authProvider = user.providerData[0]?.providerId === 'google.com' ? 'google' : 'email';

  if (!userDoc.exists()) {
    // Create new user profile
    const profile: UserProfile = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || user.email?.split('@')[0] || null,
      photoURL: user.photoURL,
      createdAt: Timestamp.now(),
      lastSeen: Timestamp.now(),
      authProvider,
      preferences: {
        theme: 'dark',
        ghostMode: true
      }
    };
    await setDoc(userRef, profile);
    console.log('[Firebase] Created user profile for:', user.email);
  } else {
    // Update last seen and photo (Google may provide updated photo)
    await updateDoc(userRef, {
      lastSeen: Timestamp.now(),
      photoURL: user.photoURL || userDoc.data()?.photoURL,
      displayName: user.displayName || userDoc.data()?.displayName
    });
  }
}

/**
 * Get user context (hot memory for instant UI state)
 */
export async function getUserContext(uid: string): Promise<UserContext | null> {
  try {
    const contextRef = doc(db, 'superjarvis_users', uid, 'context', 'current');
    const contextDoc = await getDoc(contextRef);
    return contextDoc.exists() ? contextDoc.data() as UserContext : null;
  } catch (error) {
    console.error('[Firebase] Error getting user context:', error);
    return null;
  }
}

/**
 * Update user context
 */
export async function updateUserContext(uid: string, context: Partial<UserContext>): Promise<void> {
  try {
    const contextRef = doc(db, 'superjarvis_users', uid, 'context', 'current');
    await setDoc(contextRef, context, { merge: true });
  } catch (error) {
    console.error('[Firebase] Error updating user context:', error);
  }
}

// ============================================================================
// TRUST-DRIP: Permission Keyring
// ============================================================================

interface TrustDripToken {
  provider: string;  // 'gmail', 'calendar', 'drive', etc.
  granted: boolean;
  grantedAt: Timestamp | null;
  scopes: string[];
}

/**
 * Get user's Trust-Drip keyring
 */
export async function getTrustDripKeyring(uid: string): Promise<Record<string, TrustDripToken>> {
  try {
    const keyringRef = doc(db, 'superjarvis_users', uid, 'trust_drip', 'keyring');
    const keyringDoc = await getDoc(keyringRef);
    return keyringDoc.exists() ? keyringDoc.data() as Record<string, TrustDripToken> : {};
  } catch (error) {
    console.error('[Firebase] Error getting Trust-Drip keyring:', error);
    return {};
  }
}

/**
 * Grant a Trust-Drip permission
 */
export async function grantTrustDripPermission(
  uid: string,
  provider: string,
  scopes: string[]
): Promise<void> {
  try {
    const keyringRef = doc(db, 'superjarvis_users', uid, 'trust_drip', 'keyring');
    await setDoc(keyringRef, {
      [provider]: {
        provider,
        granted: true,
        grantedAt: Timestamp.now(),
        scopes
      }
    }, { merge: true });
    console.log(`[Firebase] Trust-Drip: Granted ${provider} permission`);
  } catch (error) {
    console.error('[Firebase] Error granting Trust-Drip permission:', error);
  }
}

/**
 * Check if a Trust-Drip permission exists
 */
export async function hasTrustDripPermission(uid: string, provider: string): Promise<boolean> {
  const keyring = await getTrustDripKeyring(uid);
  return keyring[provider]?.granted || false;
}

export { auth, db, storage };
